import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import formService from '../../services/formService';

const downloadBlob = (blob, filename='submissions.csv') => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const SubmissionsTable = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [f, s] = await Promise.all([
        formService.getById(id),
        formService.listSubmissions(id)
      ]);
      setForm(f);
      setSubs(s);
    } catch (e) {
      setError(t('forms.submissions.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const fields = (form?.schema?.fields || []);
  const fieldKeys = fields.map(f => f.key);

  // Filter submissions based on search term
  const filteredSubs = subs.filter(sub => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    
    // Search in email
    if (sub.submitter_email && sub.submitter_email.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Search in data fields
    const data = sub.data || {};
    return Object.values(data).some(value => {
      if (value === null || value === undefined) return false;
      if (Array.isArray(value)) return value.join(', ').toLowerCase().includes(searchLower);
      return String(value).toLowerCase().includes(searchLower);
    });
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredSubs.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentSubs = filteredSubs.slice(startIndex, endIndex);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const exportCSV = async () => {
    try {
      const blob = await formService.exportSubmissionsCSV(id);
      downloadBlob(blob, `form_${id}_submissions.csv`);
    } catch (e) {
      alert(t('forms.submissions.exportError'));
    }
  };

  const dropForm = async () => {
    if (!form) return;
    const ok = window.confirm(t('forms.submissions.confirmDelete', { name: form.name }));
    if (!ok) return;
    try {
      await formService.remove(form.id);
      navigate('/forms');
    } catch (e) {
      alert(t('forms.submissions.deleteError'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{t('forms.submissions.errorLoading')}</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!form) return null;

  return (
    <div className="bg-white shadow-sm rounded-lg">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{t('forms.submissions.title')}</h3>
            <p className="text-sm text-gray-600">{form.name}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={exportCSV}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('forms.submissions.exportCsv')}
            </button>
            <button
              onClick={dropForm}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {t('forms.submissions.dropForm')}
            </button>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              className="pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value={5}>{t('forms.submissions.perPage', { count: 5 })}</option>
              <option value={10}>{t('forms.submissions.perPage', { count: 10 })}</option>
              <option value={25}>{t('forms.submissions.perPage', { count: 25 })}</option>
              <option value={50}>{t('forms.submissions.perPage', { count: 50 })}</option>
              <option value={100}>{t('forms.submissions.perPage', { count: 100 })}</option>
            </select>
          </div>
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={t('forms.submissions.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.submissions.table.id')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.submissions.table.created')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.submissions.table.submitterEmail')}</th>
              {fields.map(f => (
                <th key={f.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {f.label || f.key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentSubs.length === 0 ? (
              <tr>
                <td colSpan={3 + fields.length} className="px-6 py-4 text-center text-gray-500">
                  {searchTerm ? t('forms.submissions.emptySearch') : t('forms.submissions.empty')}
                </td>
              </tr>
            ) : (
              currentSubs.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{s.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(s.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.submitter_email || '-'}</td>
                  {fieldKeys.map(k => {
                    const v = (s.data || {})[k];
                    let displayValue;
                    if (Array.isArray(v)) {
                      displayValue = v.join(', ');
                    } else if (v && typeof v === 'object') {
                      displayValue = v.name || '(object)';
                    } else {
                      displayValue = v !== undefined && v !== null ? String(v) : '-';
                    }
                    return (
                      <td key={k} className="px-6 py-4 text-sm text-gray-900">
                        <div className="max-w-xs truncate" title={displayValue}>
                          {displayValue}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {filteredSubs.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {t('forms.submissions.showing', { start: startIndex + 1, end: Math.min(endIndex, filteredSubs.length), total: filteredSubs.length })}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('forms.submissions.pagination.previous')}
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNum = index + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (
                      pageNum === currentPage - 2 ||
                      pageNum === currentPage + 2
                    ) {
                      return (
                        <span key={pageNum} className="px-2 py-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('forms.submissions.pagination.next')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionsTable;
